import { useCallback, useEffect, useMemo, useState } from 'react'

import { MEETING_FORMAT_CHOICE } from '@/components/integration/nylas/scheduler/constants'
import { SchedulerConfigContextValue } from '@/components/integration/nylas/scheduler/context/SchedulerConfigContext'
import {
  SchedulerSelectionContextValue,
  SchedulerTimeRange,
} from '@/components/integration/nylas/scheduler/context/SchedulerSelectionContext'
import {
  isInPersonMeetingFormat,
  isSameAvailabilityFilter,
} from '@/components/integration/nylas/scheduler/helpers'
import useAvailabilityQuery from '@/components/integration/nylas/scheduler/hooks/useAvailabilityQuery'
import usePrefetchMonthAvailability from '@/components/integration/nylas/scheduler/hooks/usePrefetchMonthAvailability'
import usePublicMeetingTypeQuery from '@/components/integration/nylas/scheduler/hooks/usePublicMeetingTypeQuery'
import useSchedulerDays from '@/components/integration/nylas/scheduler/hooks/useSchedulerDays'
import {
  resolveInPersonMeetingFormat,
  resolveMeetingFormatChoice,
} from '@/components/integration/nylas/scheduler/meetingFormatSelection'
import {
  AvailabilityFilter,
  ClientHomeAddress,
  EMPTY_CLIENT_HOME_ADDRESS,
  EVERY_ADVISER_AVAILABILITY_FILTER,
  InPersonMeetingFormat,
  MeetingFormatChoice,
  SchedulerAvailabilitySource,
  SchedulerOwningEntityType,
  SchedulerParticipant,
  SchedulerSelection,
} from '@/components/integration/nylas/scheduler/types'
import { MEETING_FORMAT } from '@/lib/constants'
import availabilityService from '@/lib/services/availabilityService'
import { MeetingFormat } from '@/types/misc'
import { PublicMeetingTypeItem } from '@/types/responses/meeting-types'

/**
 * Whose availability to show, and under which rules. An in-person meeting
 * follows the meeting type's in-person booking rules, and is the only kind
 * tied to an office: the office it's at, or the office handling a visit to the
 * client's home. Video and phone always see every adviser's times.
 */
function getAvailabilityFilter(
  format: MeetingFormat | null,
  locationId: number | null,
): AvailabilityFilter {
  if (isInPersonMeetingFormat(format)) {
    return { locationId, inPersonFormat: format }
  }

  return EVERY_ADVISER_AVAILABILITY_FILTER
}

function getMeetingFormatForChoice(
  formatChoice: MeetingFormatChoice | null,
  inPersonFormat: InPersonMeetingFormat | null,
): MeetingFormat | null {
  switch (formatChoice) {
    case null:
      return null
    case 'video':
      return MEETING_FORMAT.video
    case 'phone':
      return MEETING_FORMAT.phone
    case 'in_person':
      return inPersonFormat
  }
}

interface Args {
  primaryParticipant: SchedulerParticipant
  owningEntityType: SchedulerOwningEntityType
  owningEntityId: number | string
  availabilitySource?: SchedulerAvailabilitySource
  isRescheduling: boolean
  /** The answers to restore, e.g. after coming back to the booking step. */
  initialSelection?: SchedulerSelection
  /** Told about every change to the answers, so the host can keep them. */
  onSelectionChange?: (selection: SchedulerSelection) => void
  suggestedClientAddress?: ClientHomeAddress | null
  /**
   * The formats and offices on offer, when the host already has them (e.g.
   * from the guest reschedule page). Otherwise they're loaded for the owning
   * entity.
   */
  meetingType?: PublicMeetingTypeItem
  hasHiddenClientHomeAddress?: boolean
  hiddenClientHomeTownOrCity?: string | null
}

interface Result {
  configValue: SchedulerConfigContextValue
  selectionValue: SchedulerSelectionContextValue
  duration: number | undefined
  isLoading: boolean
  isFetching: boolean
  isAvailabilityFetchError: boolean
  retry: () => void
}

export default function useSchedulerState({
  primaryParticipant,
  owningEntityType,
  owningEntityId,
  availabilitySource,
  isRescheduling,
  initialSelection,
  onSelectionChange,
  suggestedClientAddress = null,
  meetingType: givenMeetingType,
  hasHiddenClientHomeAddress = false,
  hiddenClientHomeTownOrCity = null,
}: Args): Result {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [selectedDateTime, setSelectedDateTime] = useState<Date | null>(null)
  const [showFullCalendar, setShowFullCalendar] = useState(false)
  const [selectedFormatChoice, setSelectedFormatChoice] =
    useState<MeetingFormatChoice | null>(initialSelection?.formatChoice ?? null)
  const [selectedInPersonFormat, setSelectedInPersonFormat] =
    useState<InPersonMeetingFormat | null>(
      initialSelection?.inPersonFormat ?? null,
    )
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(
    initialSelection?.locationId ?? null,
  )
  const [clientHomeAddress, setClientHomeAddressState] =
    useState<ClientHomeAddress>(
      initialSelection?.clientHomeAddress ?? EMPTY_CLIENT_HOME_ADDRESS,
    )
  const [selectedTimezone, setSelectedTimezone] = useState<string>(
    Intl.DateTimeFormat().resolvedOptions().timeZone,
  )
  const [guests, setGuests] = useState<SchedulerParticipant[]>([])
  const [finalPrimaryParticipant, setFinalPrimaryParticipant] =
    useState(primaryParticipant)
  const [selectedDateRange, setSelectedDateRange] =
    useState<SchedulerTimeRange>(
      availabilityService.getInitialAvailabilityDateRange(),
    )
  // Per filter, so switching format or office pre-loads the month for the new
  // one too, ready for "More dates"
  const [
    monthlyAvailabilityPrefetchedFor,
    setMonthlyAvailabilityPrefetchedFor,
  ] = useState<AvailabilityFilter | null>(null)

  const meetingTypeQuery = usePublicMeetingTypeQuery(
    owningEntityType,
    owningEntityId,
    { enabled: !givenMeetingType },
  )

  const meetingType = givenMeetingType ?? meetingTypeQuery.data
  const availableMeetingFormats = meetingType?.meetingFormats
  const availableLocations = meetingType?.locations
  const availableHomeVisitLocations = meetingType?.homeVisitLocations
  const maxBookingNoticeDays = meetingType?.maxDaysInFuture

  // The person's answers, falling back to what's offered: a choice that's no
  // longer offered gives way to the default (video, then phone, then in
  // person), and "in person" settles on a format once there's only one way to
  // read it or "Where?" has been answered.
  const resolveFormat = useCallback(
    (
      formatChoice: MeetingFormatChoice | null,
      inPersonFormat: InPersonMeetingFormat | null,
    ): {
      formatChoice: MeetingFormatChoice | null
      inPersonFormat: InPersonMeetingFormat | null
      format: MeetingFormat | null
    } => {
      if (availableMeetingFormats === undefined) {
        return { formatChoice: null, inPersonFormat: null, format: null }
      }

      const resolvedFormatChoice = resolveMeetingFormatChoice(
        formatChoice,
        availableMeetingFormats,
      )
      const resolvedInPersonFormat =
        resolvedFormatChoice === MEETING_FORMAT_CHOICE.in_person
          ? resolveInPersonMeetingFormat(
              inPersonFormat,
              availableMeetingFormats,
            )
          : null

      return {
        formatChoice: resolvedFormatChoice,
        inPersonFormat: resolvedInPersonFormat,
        format: getMeetingFormatForChoice(
          resolvedFormatChoice,
          resolvedInPersonFormat,
        ),
      }
    },
    [availableMeetingFormats],
  )

  // Pick the office automatically when there's only one to meet at, and
  // ignore a choice that's no longer offered. The office handling a visit to
  // the client's home is optional, so it's never picked automatically.
  const [firstLocation, ...otherLocations] = availableLocations ?? []
  const onlyAvailableLocationId: number | null =
    firstLocation && otherLocations.length === 0 ? firstLocation.id : null

  const resolveLocationId = useCallback(
    (
      locationId: number | null,
      format: MeetingFormat | null,
    ): number | null => {
      if (format === MEETING_FORMAT.in_person_office) {
        return (
          onlyAvailableLocationId ??
          availableLocations?.find((location) => location.id === locationId)
            ?.id ??
          null
        )
      }

      if (format === MEETING_FORMAT.in_person_client_home) {
        return (
          availableHomeVisitLocations?.find(
            (location) => location.id === locationId,
          )?.id ?? null
        )
      }

      return null
    },
    [onlyAvailableLocationId, availableLocations, availableHomeVisitLocations],
  )

  const effectiveSelection = resolveFormat(
    selectedFormatChoice,
    selectedInPersonFormat,
  )
  const effectiveSelectedFormat = effectiveSelection.format
  const effectiveSelectedLocationId = resolveLocationId(
    selectedLocationId,
    effectiveSelectedFormat,
  )

  // In-person meetings have their own length
  const durationMinutes = isInPersonMeetingFormat(effectiveSelectedFormat)
    ? meetingType?.inPersonDurationMinutes
    : meetingType?.durationMinutes

  // An in-person office meeting's times depend on the office, so don't ask for
  // any until one is chosen: the API rejects the request without it.
  const isWaitingForOffice =
    effectiveSelectedFormat === MEETING_FORMAT.in_person_office &&
    effectiveSelectedLocationId === null

  // Only the chosen office's advisers can take an in-person meeting there, so
  // only their availability is shown.
  const availabilityFilter = useMemo(
    () =>
      getAvailabilityFilter(
        effectiveSelectedFormat,
        effectiveSelectedLocationId,
      ),
    [effectiveSelectedFormat, effectiveSelectedLocationId],
  )

  const prefetchMonthAvailability = usePrefetchMonthAvailability(
    owningEntityType,
    owningEntityId,
    availabilityFilter,
    availabilitySource,
  )

  const { isLoading, isFetching, isAvailabilityFetchError, refetch } =
    useSchedulerDays(
      owningEntityType,
      owningEntityId,
      selectedDateRange.start,
      selectedDateRange.end,
      selectedTimezone,
      availabilityFilter,
      { enabled: !isWaitingForOffice, availabilitySource },
    )

  const extendedAvailabilityRange = useMemo(
    () => availabilityService.getExtendedAvailabilityDateRange(),
    [],
  )

  const shouldPrefetchExtendedAvailability =
    !isLoading && !isAvailabilityFetchError && !isWaitingForOffice

  const extendedAvailabilityQuery = useAvailabilityQuery(
    owningEntityType,
    owningEntityId,
    extendedAvailabilityRange.start,
    extendedAvailabilityRange.end,
    availabilityFilter,
    { enabled: shouldPrefetchExtendedAvailability, availabilitySource },
  )

  const isExtendedAvailabilityReady = extendedAvailabilityQuery.isSuccess

  useEffect(() => {
    if (
      !isExtendedAvailabilityReady ||
      (monthlyAvailabilityPrefetchedFor &&
        isSameAvailabilityFilter(
          monthlyAvailabilityPrefetchedFor,
          availabilityFilter,
        ))
    ) {
      return
    }

    prefetchMonthAvailability(extendedAvailabilityRange.end)
    setMonthlyAvailabilityPrefetchedFor(availabilityFilter)
  }, [
    isExtendedAvailabilityReady,
    monthlyAvailabilityPrefetchedFor,
    availabilityFilter,
    prefetchMonthAvailability,
    extendedAvailabilityRange.end,
  ])

  const selectDate = useCallback((date: Date | null) => {
    setSelectedDate(date)
    setSelectedDateTime(null)
  }, [])

  const switchToCalendar = useCallback(() => {
    setShowFullCalendar(true)
    setSelectedDate(null)
    setSelectedDateTime(null)
    setSelectedDateRange(
      availabilityService.getMonthDateRange(extendedAvailabilityRange.end),
    )
  }, [extendedAvailabilityRange.end])

  // Clear the picked date when the times on offer change (different office,
  // or switching between in-person and remote) or when there are none yet
  // (e.g. "In person" before "Where?" is answered). Switching between video
  // and phone, or re-picking the same choice, keeps the date.
  const clearDateIfTimesOnOfferChanged = useCallback(
    (nextFormat: MeetingFormat | null, nextLocationId: number | null) => {
      const nextAvailabilityFilter = getAvailabilityFilter(
        nextFormat,
        nextLocationId,
      )
      const hasTimesOnOffer = nextFormat !== null

      if (
        !hasTimesOnOffer ||
        !isSameAvailabilityFilter(nextAvailabilityFilter, availabilityFilter)
      ) {
        selectDate(null)
      }
    },
    [availabilityFilter, selectDate],
  )

  // The raw answers (not the resolved ones), so restoring them later gives the
  // same result once the meeting type has loaded again.
  const publishSelection = useCallback(
    (changes: Partial<SchedulerSelection>) => {
      onSelectionChange?.({
        formatChoice: selectedFormatChoice,
        inPersonFormat: selectedInPersonFormat,
        locationId: selectedLocationId,
        clientHomeAddress,
        ...changes,
      })
    },
    [
      onSelectionChange,
      selectedFormatChoice,
      selectedInPersonFormat,
      selectedLocationId,
      clientHomeAddress,
    ],
  )

  const selectFormatChoice = useCallback(
    (formatChoice: MeetingFormatChoice | null) => {
      setSelectedFormatChoice(formatChoice)
      publishSelection({ formatChoice })

      const nextFormat = resolveFormat(
        formatChoice,
        selectedInPersonFormat,
      ).format
      clearDateIfTimesOnOfferChanged(
        nextFormat,
        resolveLocationId(selectedLocationId, nextFormat),
      )
    },
    [
      publishSelection,
      resolveFormat,
      selectedInPersonFormat,
      clearDateIfTimesOnOfferChanged,
      resolveLocationId,
      selectedLocationId,
    ],
  )

  // The office lists differ between the two in-person formats, so an office
  // chosen for one doesn't carry over to the other.
  const selectInPersonFormat = useCallback(
    (inPersonFormat: InPersonMeetingFormat | null) => {
      setSelectedInPersonFormat(inPersonFormat)
      setSelectedLocationId(null)
      publishSelection({ inPersonFormat, locationId: null })

      const nextFormat = resolveFormat(
        selectedFormatChoice,
        inPersonFormat,
      ).format
      clearDateIfTimesOnOfferChanged(
        nextFormat,
        resolveLocationId(null, nextFormat),
      )
    },
    [
      publishSelection,
      resolveFormat,
      selectedFormatChoice,
      clearDateIfTimesOnOfferChanged,
      resolveLocationId,
    ],
  )

  const selectLocationId = useCallback(
    (locationId: number | null) => {
      setSelectedLocationId(locationId)
      publishSelection({ locationId })
      clearDateIfTimesOnOfferChanged(
        effectiveSelectedFormat,
        resolveLocationId(locationId, effectiveSelectedFormat),
      )
    },
    [
      publishSelection,
      clearDateIfTimesOnOfferChanged,
      resolveLocationId,
      effectiveSelectedFormat,
    ],
  )

  const setClientHomeAddress = useCallback(
    (nextClientHomeAddress: ClientHomeAddress) => {
      setClientHomeAddressState(nextClientHomeAddress)
      publishSelection({ clientHomeAddress: nextClientHomeAddress })
    },
    [publishSelection],
  )

  const switchToSimple = useCallback(() => {
    setShowFullCalendar(false)
    setSelectedDate(null)
    setSelectedDateTime(null)
    setSelectedDateRange(availabilityService.getInitialAvailabilityDateRange())
  }, [])

  const configValue = useMemo<SchedulerConfigContextValue>(
    () => ({
      timezone: selectedTimezone,
      setTimezone: setSelectedTimezone,
      primaryParticipant,
      owningEntityType,
      owningEntityId,
      availabilitySource,
      isRescheduling,
      availableMeetingFormats,
      availableLocations,
      availableHomeVisitLocations,
      suggestedClientAddress,
      hasHiddenClientHomeAddress,
      hiddenClientHomeTownOrCity,
      durationMinutes,
      maxBookingNoticeDays,
    }),
    [
      selectedTimezone,
      primaryParticipant,
      owningEntityType,
      owningEntityId,
      availabilitySource,
      isRescheduling,
      availableMeetingFormats,
      availableLocations,
      availableHomeVisitLocations,
      suggestedClientAddress,
      hasHiddenClientHomeAddress,
      hiddenClientHomeTownOrCity,
      durationMinutes,
      maxBookingNoticeDays,
    ],
  )

  const selectionValue = useMemo<SchedulerSelectionContextValue>(
    () => ({
      selectedDate,
      selectedDateTime,
      selectedDateRange,
      showFullCalendar,
      guests,
      finalPrimaryParticipant,
      selectedFormatChoice: effectiveSelection.formatChoice,
      selectedInPersonFormat: effectiveSelection.inPersonFormat,
      selectedFormat: effectiveSelectedFormat,
      selectedLocationId: effectiveSelectedLocationId,
      clientHomeAddress,
      availabilityFilter,
      isExtendedAvailabilityReady,
      selectDate,
      selectDateTime: setSelectedDateTime,
      setSelectedDateRange,
      switchToCalendar,
      switchToSimple,
      setGuests,
      setFinalPrimaryParticipant,
      setSelectedFormatChoice: selectFormatChoice,
      setSelectedInPersonFormat: selectInPersonFormat,
      setSelectedLocationId: selectLocationId,
      setClientHomeAddress,
    }),
    [
      selectedDate,
      selectedDateTime,
      selectedDateRange,
      showFullCalendar,
      guests,
      finalPrimaryParticipant,
      effectiveSelection.formatChoice,
      effectiveSelection.inPersonFormat,
      effectiveSelectedFormat,
      effectiveSelectedLocationId,
      clientHomeAddress,
      availabilityFilter,
      isExtendedAvailabilityReady,
      selectDate,
      switchToCalendar,
      switchToSimple,
      selectFormatChoice,
      selectInPersonFormat,
      selectLocationId,
      setClientHomeAddress,
    ],
  )

  return {
    configValue,
    selectionValue,
    duration: durationMinutes,
    isLoading,
    isFetching,
    isAvailabilityFetchError,
    retry: refetch,
  }
}
